import { transition, useStateTransition } from "@codesandbox/states";
import { useEnvironmentInterface } from "environment-interface";
import type { ApiEvent } from "environment-interface/api";
import { useEditor } from "features/editor/Feature";
import React from "react";

export type Secrets = Record<string, string>;

type SecretsState =
  | {
      state: "IDLE";
      secrets: Secrets;
    }
  | {
      state: "GETTING";
    }
  | {
      state: "PUTTING";
      secrets: Secrets;
    }
  | { state: "DELETING"; key: string; secrets: Secrets }
  | {
      state: "ERROR";
      message: string;
    };

type SecretsAction =
  | {
      type: "GET_SECRETS";
    }
  | {
      type: "PUT_SECRETS";
      secrets: Secrets;
    }
  | {
      type: "DELETE_SECRET";
      secrets: Secrets;
      key: string;
    };

const reducer = (state: SecretsState, action: SecretsAction | ApiEvent) =>
  transition(state, action, {
    IDLE: {
      GET_SECRETS: () => ({ state: "GETTING" }),
      PUT_SECRETS: (_, { secrets }) => ({ state: "PUTTING", secrets }),
      DELETE_SECRET: (_, { key, secrets }) => ({
        state: "DELETING",
        key,
        secrets,
      }),
    },
    GETTING: {
      "API:FETCH_SECRETS_SUCCESS": (_, { secrets }) => ({
        state: "IDLE",
        secrets,
      }),
      "API:FETCH_SECRETS_ERROR": (_, { error }) => ({
        state: "ERROR",
        message: error,
      }),
    },
    PUTTING: {
      "API:PUT_SECRETS_SUCCESS": (_, { secrets }) => ({
        state: "IDLE",
        secrets,
      }),
      "API:PUT_SECRETS_ERROR": (_, { error }) => ({
        state: "ERROR",
        message: error,
      }),
    },
    DELETING: {
      // A delete is either a put for projects or an actual delete for sandboxes
      "API:PUT_SECRETS_SUCCESS": (_, { secrets }) => ({
        state: "IDLE",
        secrets,
      }),
      "API:PUT_SECRETS_ERROR": (_, { error }) => ({
        state: "ERROR",
        message: error,
      }),
      "API:DELETE_SECRET_SUCCESS": (_, { secrets }) => ({
        state: "IDLE",
        secrets,
      }),
      "API:DELETE_SECRET_ERROR": (_, { error }) => ({
        state: "ERROR",
        message: error,
      }),
    },
    ERROR: {
      GET_SECRETS: () => ({ state: "GETTING" }),
    },
  });

export const useEnvironmentVariables = (): [
  SecretsState,
  React.Dispatch<SecretsAction>,
] => {
  const { api } = useEnvironmentInterface();
  const editor = useEditor();

  const secretsReducer = React.useReducer(reducer, {
    state: "IDLE",
    secrets: {},
  });
  const [state, dispatch] = secretsReducer;

  React.useEffect(() => api.events.subscribe(dispatch), []);

  useStateTransition(state, "PUTTING", ({ secrets }) => {
    if (editor.type === "sandbox") {
      api.putSandboxSecrets(editor.id, secrets);
    } else {
      api.putProjectSecrets(
        editor.owner,
        editor.repo,
        editor.workspace?.id ?? null,
        secrets,
      );
    }
  });

  useStateTransition(state, "GETTING", () => {
    api.getSecrets(
      editor.type === "sandbox"
        ? {
            type: editor.type,
            sandboxId: editor.id,
          }
        : {
            type: editor.type,
            owner: editor.owner,
            repo: editor.repo,
          },
      editor.workspace?.id ?? null,
    );
  });

  useStateTransition(state, "DELETING", ({ key, secrets }) => {
    if (editor.type === "sandbox") {
      api.deleteSandboxSecret(editor.id, key);
    } else {
      api.putProjectSecrets(
        editor.owner,
        editor.repo,
        editor.workspace?.id ?? null,
        secrets,
      );
    }
  });

  return secretsReducer;
};
